import { CircularProgress, DropListButton, TabSegmented, TabSegmentedGroup } from '@ketch-com/deck'
import { Box, Typography, styled } from '@mui/material'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { PreviewDisplayMode } from '../../../utils/enums'
import { DeploymentExperienceTypeLabels, previewDisplayModeLabels } from '../../../utils/labels'
import { LanguageWithStatusDTO } from 'interfaces/languages/language'
import { PolicyScopeDTO } from 'interfaces/policyScopes-v2/policyScope'
import { ExperiencePreviewContext } from '../context/ExperiencePreviewContext'
import { DeploymentPlanDTO } from 'interfaces/deploymentPlans-v2/deploymentPlan'
import { ExperienceType } from 'interfaces/experiences/experienceType'
import { DeploymentExperienceType } from '@ketch-sdk/ketch-types'

const PreviewHeaderBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '12px 16px',
  boxShadow: '0px 4px 10px 0px rgba(7, 26, 36, 0.10);',
  zIndex: 3,
})

const PreviewHeaderLeftBox = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-end',
})

const PreviewHeaderRightBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
})

interface Props {
  isLoading: boolean
  deploymentPlan?: DeploymentPlanDTO
}

export const DeploymentPreviewHeader: React.FC<Props> = ({ isLoading, deploymentPlan }) => {
  const {
    language,
    isLanguagesLoading,
    deploymentExperienceType,
    setDeploymentExperienceType,
    displayMode,
    setDisplayMode,
    languageOptions,
    setLanguage,
    jurisdictionOptions: allJurisdictions,
    jurisdiction,
    setJurisdiction,
    themeOptions,
    setTheme,
    setIsPreviewReady,
    currentPreviewConfig,
  } = useContext(ExperiencePreviewContext)

  const [jurisdictionOptions, setJurisdictionOptions] = useState<PolicyScopeDTO[]>([])
  const hidePreview = useMemo(() => {
    const experience =
      deploymentExperienceType === DeploymentExperienceType.AutoInitiated
        ? currentPreviewConfig?.experiences?.autoInitiated
        : currentPreviewConfig?.experiences?.userInitiated

    return !Object.keys(experience || {}).length && !!currentPreviewConfig
  }, [currentPreviewConfig, deploymentExperienceType])

  useEffect(() => {
    if (languageOptions?.length) {
      setLanguage(languageOptions[0])
    }
  }, [languageOptions, setLanguage])

  useEffect(() => {
    if (jurisdictionOptions?.length) {
      setJurisdiction(jurisdictionOptions[0])
    }
  }, [jurisdictionOptions, setJurisdiction])

  useEffect(() => {
    if (themeOptions?.length) {
      setTheme(themeOptions[0])
    }
  }, [themeOptions, setTheme])

  useEffect(() => {
    const requiredExperienceType =
      deploymentExperienceType === DeploymentExperienceType.AutoInitiated
        ? ExperienceType.CONSENT
        : ExperienceType.PREFERENCE
    const jurisdictionHasDeploymentExperienceType = !!deploymentPlan?.policyScopeExperiences.some(
      e => e.policyScopeCode === jurisdiction?.code && (e.experienceType as any) === requiredExperienceType,
    )
    setJurisdictionOptions(allJurisdictions || [])
    if (!jurisdictionHasDeploymentExperienceType) {
      setIsPreviewReady(true)
    }
  }, [
    allJurisdictions,
    deploymentExperienceType,
    deploymentPlan?.policyScopeExperiences,
    jurisdiction?.code,
    setIsPreviewReady,
  ])

  return (
    <PreviewHeaderBox>
      <PreviewHeaderLeftBox>
        {/* Deployment experience type picker */}
        <TabSegmentedGroup
          value={deploymentExperienceType}
          onChange={(event: React.MouseEvent<HTMLElement>, newDeploymentExperienceType: DeploymentExperienceType) =>
            setDeploymentExperienceType(newDeploymentExperienceType)
          }
          exclusive
          size={'small'}
          sx={{ height: 'fit-content' }}
        >
          {Object.values(DeploymentExperienceType).map(depExpType => (
            <TabSegmented value={depExpType} key={depExpType}>
              <Typography
                // Different text variant for selected
                variant={deploymentExperienceType === depExpType ? 'smallLabel' : 'smallBody'}
              >
                {DeploymentExperienceTypeLabels[depExpType]}
              </Typography>
            </TabSegmented>
          ))}
        </TabSegmentedGroup>

        {/* Display mode picker */}
        <TabSegmentedGroup
          value={displayMode}
          onChange={(event: React.MouseEvent<HTMLElement>, newDisplayMode: PreviewDisplayMode) => {
            setIsPreviewReady(false)
            setDisplayMode(newDisplayMode)
          }}
          exclusive
          size={'small'}
          sx={{ height: 'fit-content' }}
        >
          {Object.values(PreviewDisplayMode).map(dp => (
            <TabSegmented value={dp} key={dp} sx={{ px: '20px' }}>
              <Typography
                // Different text variant for selected
                variant={dp === displayMode ? 'smallLabel' : 'smallBody'}
              >
                {previewDisplayModeLabels[dp]}
              </Typography>
            </TabSegmented>
          ))}
        </TabSegmentedGroup>

        {/* Language picker */}
        {!isLanguagesLoading && (
          <DropListButton
            label="Language"
            size="small"
            width="200px"
            options={languageOptions ?? []}
            onChange={(_, newValue) => {
              setIsPreviewReady(false)
              setLanguage(newValue as LanguageWithStatusDTO)
            }}
            value={language ?? { language: { englishName: 'English', code: '', nativeName: '' }, enabled: true }}
            getOptionLabel={l => l.language.englishName}
          />
        )}

        {/* Jurisdiction picker */}
        <DropListButton
          label="Jurisdiction"
          size="small"
          width="200px"
          options={jurisdictionOptions ?? []}
          onChange={(_, newValue) => {
            setIsPreviewReady(false)
            setJurisdiction(newValue as PolicyScopeDTO)
          }}
          value={jurisdiction ?? { code: '', name: 'Global Standard', description: '' }}
          getOptionLabel={j => j.name}
        />
      </PreviewHeaderLeftBox>
      <PreviewHeaderRightBox>
        {isLoading ? (
          <CircularProgress label={<Typography variant={'label'}>Preview Loading</Typography>} />
        ) : (
          <>
            {hidePreview && (
              <Typography>
                No <Typography variant={'label'}>{DeploymentExperienceTypeLabels[deploymentExperienceType]}</Typography>{' '}
                Experience for this Jurisdiction
              </Typography>
            )}
          </>
        )}
      </PreviewHeaderRightBox>
    </PreviewHeaderBox>
  )
}
